import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleRotationGroup } from './article-rotation-group';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { PropertyUtils } from '../util/property-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { CompositeArticleUtils } from '../util/composite-article-utils';
import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositeArticleInfoUtils } from '../util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class RotateArticleUtils {
  constructor(private containerService: ContainerService,
              private compositeArticleUtils: CompositeArticleUtils,
              private articleContainerUtils: ArticleContainerUtils) {}

  /**
   * Returns common rotation angles for all containers.
   *
   * @param articleContainers article containers
   * @return array of rotation angles
   */
  private findArticlesRotations(articleContainers: ArticleContainer[]): number[] {
    if (!articleContainers || articleContainers.length === 0) {
      return [];
    }

    const rotations: number[] = RotateArticleUtils.getAvailableArticleRotations(articleContainers[0]);
    if (rotations.length === 0) {
      return [];
    }

    for (let i: number = 1; i < articleContainers.length; i++) {
      const articleAngles: number[] = RotateArticleUtils.getAvailableArticleRotations(articleContainers[i]);
      for (const rotation of rotations.concat()) {
        if (articleAngles.indexOf(rotation) < 0) {
          rotations.splice(rotations.indexOf(rotation), 1);
        }
      }
    }

    return rotations;
  }

  public static findArticlesSwitchRotation(articleContainers: ArticleContainer[]): number {
    if (!articleContainers || articleContainers.length === 0) {
      return NaN;
    }
    let switchRotation: number = this.getAvailableArticleSwitchRotation(articleContainers[0]);
    if (NumberUtils.isNotNumber(switchRotation)) {
      return NaN;
    }
    for (let i: number = 1; i < articleContainers.length; i++) {
      const articleSwitchRotation: number = this.getAvailableArticleSwitchRotation(articleContainers[i]);
      if (NumberUtils.isNotNumber(articleSwitchRotation)) {
        return NaN;
      }
      if (NumberUtils.isNumber(switchRotation) && switchRotation !== articleSwitchRotation) {
        return NaN;
      } else {
        switchRotation = articleSwitchRotation;
      }
    }
    return switchRotation;
  }

  public findGroupedArticlesRotations(containers: ArticleContainer[]): number[] {
    const rotationGroups: ArticleRotationGroup[] = this.createArticleRotationGroups(containers);
    return this.findArticlesRotationsByGroups(rotationGroups);
  }

  public findArticlesRotationsByGroups(rotationGroups: ArticleRotationGroup[]): number[] {
    if (!rotationGroups) {
      return [];
    }
    const articleContainers: ArticleContainer[] = [];
    for (const group of rotationGroups) {
      ArrayUtils.mergeArrays(articleContainers, group.articleContainers);
    }
    return this.findArticlesRotations(articleContainers);
  }

  public findGroupedArticlesSwitchRotation(containers: ArticleContainer[]): number {
    const rotationGroups: ArticleRotationGroup[] = this.createArticleRotationGroups(containers);
    return RotateArticleUtils.findArticlesSwitchRotationByGroups(rotationGroups);
  }

  public static findArticlesSwitchRotationByGroups(rotationGroups: ArticleRotationGroup[]): number {
    if (!rotationGroups) {
      return NaN;
    }
    const articleContainers: ArticleContainer[] = [];
    for (const group of rotationGroups) {
      ArrayUtils.mergeArrays(articleContainers, group.articleContainers);
    }
    return this.findArticlesSwitchRotation(articleContainers);
  }

  /**
   * Method returns available rotations of article by rotations of giving connections.
   *
   * @param container article container which needs to be checked for available rotation angles
   * @return array of available rotation angles
   *
   */
  public static getAvailableArticleRotations(container: ArticleContainer): number[] {
    let rotationAngles: number[] = [];
    if (container) {
      const givingAreaIds: MetaElementUsageIdentityVO[] = container.getCurrentArticleGivingAreaIds();
      if (givingAreaIds.length > 0) {
        const metaElements: MetaElementVO[] = [];
        for (const identity of givingAreaIds) {
          const metaElement: MetaElementVO = container.findMetaElementByName(identity.metaElementName);
          if (metaElement && metaElement instanceof ConnectionAreaVO) {
            metaElements.push(metaElement);
          }
        }
        rotationAngles = PropertyUtils.getRotationAngles(metaElements);
      }
    }

    return rotationAngles;
  }

  public static getAvailableArticleSwitchRotation(container: ArticleContainer): number {
    if (container) {
      const givingAreaIds: MetaElementUsageIdentityVO[] = container.getCurrentArticleGivingAreaIds();
      if (givingAreaIds.length > 0) {
        const metaElements: MetaElementVO[] = [];
        for (const identity of givingAreaIds) {
          const metaElement: MetaElementVO = container.findMetaElementByName(identity.metaElementName);
          if (metaElement && metaElement instanceof ConnectionAreaVO) {
            metaElements.push(metaElement);
          }
        }
        return PropertyUtils.getSwitchRotationAngle(metaElements);
      }
    }
    return NaN;
  }

  public createArticleRotationGroups(containers: ArticleContainer[]): ArticleRotationGroup[] {
    if (!containers) {
      return [];
    }
    const rotationGroups: ArticleRotationGroup[] = [];
    const containerUsageIds: string[] = ArticleContainerUtils.extractArticleGUIDs(containers);
    const processedCompositeUsageIds: string[] = [];
    for (const container of containers) {
      const compositeUsageId: string = CompositeArticleInfoUtils.getCompositeArticleUsageId(container);
      if (compositeUsageId && processedCompositeUsageIds.indexOf(compositeUsageId) >= 0) {
        continue;
      }
      let containersToRotate: ArticleContainer[] = [container];
      let rotateAsGroup: boolean = false;
      if (compositeUsageId) {
        processedCompositeUsageIds.push(compositeUsageId);
        const compositePartIds: string[] = this.containerService.getCompositePartArticleUsageIds(container);
        const compositePartContainers: ArticleContainer[] = ArticleContainerUtils.findArticleContainerByGUIDs(compositePartIds, containers);
        rotateAsGroup = this.compositeArticleUtils.isAllBaseCompositePartsOfArticleIncluded(container, containerUsageIds);
        containersToRotate = rotateAsGroup ? ArticleContainerUtils.getBaseArticleContainers(compositePartContainers) : compositePartContainers;
      }
      rotationGroups.push(new ArticleRotationGroup(containersToRotate, rotateAsGroup));
    }
    return rotationGroups;
  }

  public combineLinkedRotationGroups(articleRotationGroups: ArticleRotationGroup[]): ArticleRotationGroup[] {
    const result: ArticleRotationGroup[] = [];
    for (const articleRotationGroup of articleRotationGroups) {
      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.enumerateCombinedArticlesForContainers(
        articleRotationGroup.articleContainers
      );
      articleRotationGroup.data = combinedArticles;
    }
    for (const articleRotationGroup of articleRotationGroups) {
      if (articleRotationGroup.articleContainers == null || articleRotationGroup.articleContainers.length === 0) {
        continue;
      }
      let matchFound: boolean = false;
      let resultGroup: any;
      for (resultGroup in result) {
        if (ArrayUtils.sourceArrayContainsElementFromTargetArray(resultGroup.data, articleRotationGroup.data)) {
          resultGroup.articleContainers = resultGroup.articleContainers.concat(articleRotationGroup.articleContainers);
          resultGroup.rotateAsGroup = true;
          matchFound = true;
          break;
        }
      }
      if (!matchFound) {
        result.push(articleRotationGroup);
      }
    }
    return result;
  }
}
